import { useCallback, useRef } from 'react';
import { getEventStreamFromText } from './ServicesUtils';
import { toast } from 'react-toastify';

type SpeechStreamOptions = {
    url: string;
    content: string;
    onComplete?: () => void;
    onError?: (error: unknown) => void;
};

export const useSpeechStream = () => {
    const isPlayingRef = useRef(false);

    const startSpeechStream = useCallback(async ({ url, content, onComplete, onError }: SpeechStreamOptions) => {
        if (isPlayingRef.current) {
            toast.warn('Vui lòng chờ thực hiện xong lần gọi trước');
            return;
        }

        isPlayingRef.current = true;

        try {
            const formData = new FormData();
            formData.append('text', content);

            const response = await getEventStreamFromText(url, formData);

            if (!response.body) throw new Error('Không có response body');

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let buffer = '';
            const blobs: Blob[] = [];

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const events = buffer.split('\n\n');

                for (let i = 0; i < events.length - 1; i++) {
                    const lines = events[i].split('\n');
                    const dataLine = lines.find((line) => line.startsWith('data:'));
                    if (!dataLine) continue;

                    const jsonStr = dataLine.replace('data:', '').trim();
                    const data = JSON.parse(jsonStr);

                    if (data.clip) {
                        const binary = atob(data.clip);
                        const byteArray = new Uint8Array(binary.length);
                        for (let j = 0; j < binary.length; j++) {
                            byteArray[j] = binary.charCodeAt(j);
                        }

                        blobs.push(new Blob([byteArray], { type: 'audio/wav' }));
                    }

                    if (data.status === 'complete') {
                        break;
                    }
                }

                buffer = events[events.length - 1];
            }

            // phát lần lượt từng blob
            for (const blob of blobs) {
                await new Promise<void>((resolve) => {
                    const audio = new Audio(URL.createObjectURL(blob));
                    audio.onended = () => resolve();
                    audio.onerror = () => resolve();
                    audio.play().catch(() => resolve());
                });
            }

            onComplete?.();
        } catch (err) {
            console.error(err);
            onError?.(err);
        } finally {
            isPlayingRef.current = false;
        }
    }, []);

    return { startSpeechStream };
};
